import {
  EnvironmentProviders,
  ErrorHandler,
  inject,
  makeEnvironmentProviders,
} from '@angular/core';
import { NavigationError } from '@angular/router';

import { StaleBuildService } from '../../services/stale-build.service';

/**
 * Two places see a failed chunk load, and both are watched:
 *
 * - the router, when a `loadChildren` / `loadComponent` of any route — the
 *   shell's own or one inside a module — rejects (`staleBuildNavigationErrorHandler`,
 *   to be passed to `withNavigationErrorHandler`);
 * - the global `ErrorHandler`, for every other `import()` — a module fetching
 *   a screen by hand, an unhandled navigation promise, and so on.
 *
 * Neither swallows anything. The navigation still fails and the error is still
 * logged the way it was before; the handlers only raise the flag the banner
 * reads. Overlap between the two is harmless — the flag is idempotent.
 */
export function provideStaleBuildDetection(): EnvironmentProviders {
  return makeEnvironmentProviders([{ provide: ErrorHandler, useClass: StaleBuildErrorHandler }]);
}

/**
 * Runs in injection context, so the service can be injected on the spot.
 * Returns nothing on purpose: a `RedirectCommand` would take the person away
 * from the address they asked for, and the banner is enough of an answer.
 */
export function staleBuildNavigationErrorHandler(navigationError: NavigationError): void {
  void inject(StaleBuildService).report(navigationError.error);
}

class StaleBuildErrorHandler extends ErrorHandler {
  private readonly staleBuild = inject(StaleBuildService);

  override handleError(error: unknown): void {
    void this.staleBuild.report(unwrap(error));
    super.handleError(error);
  }
}

/**
 * Errors reaching the global handler from a rejected promise or a zone task
 * are often wrapped, with the original under `rejection` — the same shape
 * Angular's own handler unwraps before logging.
 */
function unwrap(error: unknown): unknown {
  if (error && typeof error === 'object' && 'rejection' in error) {
    return (error as { rejection: unknown }).rejection;
  }

  return error;
}
